import {
    Box,
    Flex,
    Text,
    Image,
    IconButton,
    Stack,
    Collapse,
    Icon,
    Link,
    Popover,
    PopoverTrigger,
    PopoverContent,
    useDisclosure,
} from '@chakra-ui/react';
import {
    HamburgerIcon,
    CloseIcon,
    ChevronDownIcon,
    ChevronRightIcon,
} from '@chakra-ui/icons';

import { Link as ReachLink } from "react-router-dom";
import LanguageToggle from './LanguageToggle';
import { useLanguage } from '../i18n/LanguageContext';
import { LANGUAGE_SWITCH_ENABLED } from '../i18n/config';

const NavBar = () => {
    const { isOpen, onToggle } = useDisclosure();
    const { t } = useLanguage();

    return (
        <Box>
            <Flex
                bg={'#437070'}
                color={'white'}
                minH={'60px'}
                py={{ base: 2 }}
                px={{ base: 4 }}
                borderBottom={1}
                borderStyle={'solid'}
                borderColor={'gray.200'}
                align={'center'}>
                <Flex
                    flex={{ base: '0 0 40px', xl: '0 0 auto' }}
                    ml={{ base: -2 }}
                    display={{ base: 'flex', xl: 'none' }}
                    justify="center">
                    <IconButton
                        onClick={onToggle}
                        icon={
                            isOpen ? <CloseIcon w={3} h={3} /> : <HamburgerIcon w={5} h={5} />
                        }
                        variant={'ghost'}
                        aria-label={t(isOpen ? 'common.closeNavigation' : 'common.openNavigation')}
                    />
                </Flex>
                <Flex flex={{ base: 1 }} justify={{ base: 'center', xl: 'start' }} alignItems={'center'} minW={0}>
                    <Flex w={{ base: '145px', xl: '180px' }} flexShrink={0} ml={{ xl: 4 }}>
                        <Image src="/logo_new.png" alt={t('home.title')} />
                    </Flex>
                    <Flex display={{ base: 'none', xl: 'flex' }} ml={{ xl: 8 }}>
                        <DesktopNav />
                    </Flex>
                </Flex>
                {LANGUAGE_SWITCH_ENABLED && <LanguageToggle />}
                {!LANGUAGE_SWITCH_ENABLED && <Box w={{ base: '40px', xl: 0 }} flexShrink={0} />}
            </Flex>

            <Collapse in={isOpen} animateOpacity>
                <MobileNav />
            </Collapse>
        </Box>
    );
}

const DesktopNav = () => {
    const { isEnglish, t } = useLanguage();
    const navItems = getNavItems(t, isEnglish);
    const linkColor = '#eff5f5';
    const linkHoverColor = '#70a9a9';
    const popoverContentBgColor = '#2f4f4f';

    return (
        <Stack direction={'row'} spacing={2}>
            {navItems.map((navItem) => (
                <Box key={navItem.label}>
                    <Popover trigger={'hover'} placement={'bottom-start'}>
                        <PopoverTrigger>
                            <Link
                                as={ReachLink}
                                p={2}
                                to={navItem.href ?? '#'}
                                fontSize={'md'}
                                fontWeight={'bold'}
                                color={linkColor}
                                _hover={{
                                    textDecoration: 'none',
                                    color: linkHoverColor,
                                }}>
                                {navItem.label}
                            </Link>
                        </PopoverTrigger>

                        {navItem.children && (
                            <PopoverContent
                                border={0}
                                boxShadow={'xl'}
                                bg={popoverContentBgColor}
                                p={4}
                                rounded={'xl'}
                                minW={'sm'}>
                                <Stack>
                                    {navItem.children.map((child) => (
                                        <DesktopSubNav key={child.label} {...child} />
                                    ))}
                                </Stack>
                            </PopoverContent>
                        )}
                    </Popover>
                </Box>
            ))}
        </Stack>
    );
};

const DesktopSubNav = ({ label, href, subLabel }: NavItem) => {
    return (
        <Link
            as={ReachLink}
            to={href ?? '#'}
            role={'group'}
            display={'block'}
            p={2}
            rounded={'md'}
            _hover={{ bg: '#9fc6c6' }}>
            <Stack direction={'row'} align={'center'}>
                <Box>
                    <Text
                        transition={'all .3s ease'}
                        _groupHover={{ color: '#eff5f5' }}
                        fontWeight={500}>
                        {label}
                    </Text>
                    <Text fontSize={'sm'}>{subLabel}</Text>
                </Box>
                <Flex
                    transition={'all .3s ease'}
                    transform={'translateX(-10px)'}
                    opacity={0}
                    _groupHover={{ opacity: '100%', transform: 'translateX(0)' }}
                    justify={'flex-end'}
                    align={'center'}
                    flex={1}>
                    <Icon color={'#eff5f5'} w={5} h={5} as={ChevronRightIcon} />
                </Flex>
            </Stack>
        </Link>
    );
};

const MobileNav = () => {
    const { isEnglish, t } = useLanguage();
    const navItems = getNavItems(t, isEnglish);
    return (
        <Stack
            bg={'white'}
            p={4}
            display={{ xl: 'none' }}>
            {navItems.map((navItem) => (
                <MobileNavItem key={navItem.label} {...navItem} />
            ))}
        </Stack>
    );
};

const MobileNavItem = ({ label, children, href }: NavItem) => {
    const { isOpen, onToggle } = useDisclosure();

    return (
        <Stack spacing={4} onClick={children && onToggle}>
            <Flex
                py={2}
                as={ReachLink}
                to={href ?? '#'}
                justify={'space-between'}
                align={'center'}
                _hover={{
                    textDecoration: 'none',
                }}>
                <Text
                    fontWeight={600}
                    color={'gray.600'}>
                    {label}
                </Text>
                {children && (
                    <Icon
                        as={ChevronDownIcon}
                        transition={'all .25s ease-in-out'}
                        transform={isOpen ? 'rotate(180deg)' : ''}
                        w={6}
                        h={6}
                    />
                )}
            </Flex>

            <Collapse in={isOpen} animateOpacity style={{ marginTop: '0!important' }}>
                <Stack
                    mt={2}
                    pl={4}
                    borderLeft={1}
                    borderStyle={'solid'}
                    borderColor={'gray.200'}
                    align={'start'}>
                    {children &&
                        children.map((child) => (
                            <Link as={ReachLink} key={child.label} py={2} to={child.href}>
                                {child.label}
                            </Link>
                        ))}
                </Stack>
            </Collapse>
        </Stack>
    );
};
export default NavBar;

interface NavItem {
    label: string;
    subLabel?: string;
    children?: Array<NavItem>;
    href?: string;
}

const getNavItems = (t, isEnglish): Array<NavItem> => [
    {
        label: t('nav.about'),
        href: '/',
    },
    {
        label: t('nav.organization'),
        href: '/organization',
        children: [
            {
                label: t('nav.formerDirectors'),
                href: '/chief',
            },
        ]
    },
    {
        label: t('nav.research'),
        href: '/research',
        children: [
            {
                label: t('nav.fintech'),
                href: '/finTech',
            },
            {
                label: t('nav.finance'),
                href: '/finBuss',
            },
            {
                label: t('nav.law'),
                href: '/finLaw',
            },
            {
                label: t('nav.industry'),
                href: '/industry',
            },
        ],
    },
    ...(!isEnglish ? [{
        label: t('nav.advisory'),
        href: '/advisory',
    }] : []),
     // {
    //     label: '相關研究',
    //     href: '/report',
    // },
    {
        label: t('nav.activitiesAndAchievements'),
        href: '/activity',
    },
    {
        label: t('nav.interviews'),
        href: '/interview',
    },
    {
        label: t('nav.contact'),
        href: '/contact',
    },
];
